// AdminPartnerPOS — punto de venta para que el admin (o cajero) cree un
// pedido a nombre de un cliente directamente desde la web.
//   Ruta: /admin/partners/:partnerId/pos
//
// Flujo:
// 1. Carga el partner por id y su inventario.
// 2. Busca un cliente (autocompletar por nombre/email/teléfono) o usa walk-in.
// 3. Agrega productos al carrito con cantidades. Calcula totales.
// 4. Opciones: método de pago, marcar pagado, asignar conductor inicial.
// 5. Crea el partnerOrder via api.createPartnerOrder con status PENDING
//    o CONFIRMED. El flujo de cocina/preparación sigue normal.
//
// Solo PURCHASE por ahora. Para reservas STAY ya existe el flujo de checkout
// del cliente y un admin puede crearlas desde EditPartner si fuera necesario.

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from 'common';
import {
  ORDER_STATUS, ORDER_TYPES, isStayPartnerType, accentColorForType,
} from 'common/src/other/PartnerUtils';
import {
  Box, Paper, Stack, Typography, TextField, MenuItem, Button, IconButton,
  Avatar, Chip, Divider, Grid, Autocomplete, Alert, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FONT_FAMILY, MAIN_COLOR } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';

export default function AdminPartnerPOS() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const inventorydata = useSelector((s) => s.inventorydata);
  const staticusers = useSelector((s) => s.usersdata?.staticusers) || [];
  const {
    fetchPartnerById, fetchInventory, createPartnerOrder, assignDriverToPartnerOrder,
    fetchUsersOnce,
  } = api;

  const [partner, setPartner] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(true);
  const [cart, setCart] = useState([]);          // [{ item, qty }]
  const [customer, setCustomer] = useState(null); // user record o null para walk-in
  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paid, setPaid] = useState(false);
  const [confirmNow, setConfirmNow] = useState(true);   // saltar PENDING e ir directo a CONFIRMED
  const [assignedDriver, setAssignedDriver] = useState(null);
  const [dropAddress, setDropAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    if (!partnerId) return;
    setLoadingPartner(true);
    dispatch(fetchPartnerById(partnerId))
      .then((p) => setPartner(p))
      .finally(() => setLoadingPartner(false));
    dispatch(fetchInventory(partnerId));
    if (fetchUsersOnce) dispatch(fetchUsersOnce());
  }, [partnerId, dispatch, fetchPartnerById, fetchInventory, fetchUsersOnce]);

  const inventory = useMemo(
    () => (inventorydata?.byPartnerId?.[partnerId] || [])
      .filter(it => it.active !== false)
      // STAY partner tendría ROOM — el POS hoy es solo PRODUCT.
      .filter(it => it.inventoryType !== 'ROOM'),
    [inventorydata, partnerId],
  );

  const filteredInventory = useMemo(() => {
    if (!productSearch) return inventory;
    const q = productSearch.toLowerCase();
    return inventory.filter(it =>
      (it.name || '').toLowerCase().includes(q) ||
      (it.sku || '').toLowerCase().includes(q) ||
      (it.category || '').toLowerCase().includes(q)
    );
  }, [inventory, productSearch]);

  const customers = useMemo(
    () => staticusers.filter(u => u.usertype === 'customer'),
    [staticusers],
  );

  const drivers = useMemo(
    () => staticusers.filter(u => u.usertype === 'driver' && u.approved !== false),
    [staticusers],
  );

  // Totales.
  const totals = useMemo(() => {
    const subtotal = cart.reduce((acc, { item, qty }) =>
      acc + Number(item.price || 0) * Number(qty || 0), 0);
    // Tax y delivery se podrían computar con settings; para esta v1 los dejamos en 0.
    const total = subtotal;
    return { subtotal, total };
  }, [cart]);

  // Helpers.
  const addToCart = (item) => {
    setCart(prev => {
      const found = prev.find(c => c.item.id === item.id);
      if (found) {
        return prev.map(c => c.item.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      }
      return [...prev, { item, qty: 1 }];
    });
  };
  const changeQty = (itemId, delta) => {
    setCart(prev => prev.map(c => c.item.id === itemId
      ? { ...c, qty: Math.max(1, c.qty + delta) } : c));
  };
  const removeFromCart = (itemId) => setCart(prev => prev.filter(c => c.item.id !== itemId));

  const canCreate = (() => {
    if (!partner) return false;
    if (cart.length === 0) return false;
    if (!customer && !walkInName.trim()) return false;
    return true;
  })();

  const onCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      // Items snapshot para el order (lo que ya espera el shell).
      const items = cart.map(({ item, qty }) => ({
        inventoryId: item.id,
        name: item.name,
        unitPrice: Number(item.price || 0),
        qty,
        subtotal: Number(item.price || 0) * qty,
      }));

      const payload = {
        customer: customer?.id || null,
        customerName: customer
          ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
          : walkInName.trim(),
        customerPhone: customer?.mobile || walkInPhone.trim(),
        partnerId,
        partnerType: partner.partnerType,
        partnerName: partner.businessName,
        partnerCover: partner.images?.cover || null,
        selfService: false,    // POS administrado: el negocio prepara
        orderType: ORDER_TYPES.PURCHASE,
        status: confirmNow ? ORDER_STATUS.CONFIRMED : ORDER_STATUS.PENDING,
        zoneId: partner.zoneId || null,
        currency: partner.currency || 'USD',
        subtotal: totals.subtotal,
        total: totals.total,
        payment_mode: paymentMode,
        paid,
        notes: notes.trim(),
        items,
        // Para que el rider/driver vea destino si lo hay.
        drop: dropAddress.trim() ? { add: dropAddress.trim() } : null,
        // Auditoría: quién creó el pedido desde el POS.
        createdBy: auth.profile?.uid || 'admin',
        source: 'admin_pos',
      };

      const orderId = await dispatch(createPartnerOrder(payload));

      // Asignación opcional de driver al crear.
      if (assignedDriver && orderId) {
        await dispatch(assignDriverToPartnerOrder(orderId, assignedDriver.id, assignedDriver));
      }

      setCreatedOrderId(orderId);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.message || 'No pudimos crear el pedido.');
    } finally {
      setCreating(false);
    }
  };

  if (loadingPartner) return <CircularLoading />;
  if (!partner) {
    return (
      <Box sx={{ p: 3, fontFamily: FONT_FAMILY }}>
        <Alert severity="error">Negocio no encontrado.</Alert>
      </Box>
    );
  }

  // Caso especial: si es hotel/motel, el POS PURCHASE no aplica; sugerir
  // el flujo de reserva del web (EditPartner / BrowsePartners).
  if (isStayPartnerType(partner.partnerType)) {
    return (
      <Box sx={{ p: 3, fontFamily: FONT_FAMILY }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>POS · {partner.businessName}</Typography>
        </Stack>
        <Alert severity="info">
          Este negocio es <strong>{partner.partnerType}</strong>. El POS está pensado para ventas
          tipo PURCHASE (restaurante/super/tienda/farmacia). Para crear reservas usa el
          flujo del cliente desde el catálogo público.
        </Alert>
      </Box>
    );
  }

  // Pantalla de éxito.
  if (createdOrderId) {
    return (
      <Box sx={{ p: 3, fontFamily: FONT_FAMILY, maxWidth: 520, mx: 'auto', textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Pedido creado</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {confirmNow ? 'El negocio ya recibió el pedido y puede comenzar la preparación.' : 'El negocio debe confirmarlo desde su app.'}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button variant="outlined" onClick={() => navigate(`/myorders/${createdOrderId}`)}>
            Ver detalle
          </Button>
          <Button
            variant="contained" sx={{ backgroundColor: MAIN_COLOR }}
            onClick={() => {
              setCart([]); setCustomer(null); setWalkInName(''); setWalkInPhone('');
              setAssignedDriver(null); setDropAddress(''); setNotes('');
              setPaid(false); setCreatedOrderId(null);
            }}
          >
            Nuevo pedido
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <PointOfSaleIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>POS · {partner.businessName}</Typography>
          <Chip
            size="small"
            label={partner.partnerType}
            sx={{
              backgroundColor: `${accentColorForType(partner.partnerType)}1a`,
              color: accentColorForType(partner.partnerType),
              fontWeight: 600,
            }}
          />
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {/* IZQ: catálogo */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <TextField
              fullWidth size="small" placeholder="Buscar producto, SKU o categoría…"
              value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
            />
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Grid container spacing={1}>
              {filteredInventory.length === 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {inventory.length === 0 ? 'Este negocio aún no tiene productos.' : 'Sin coincidencias.'}
                  </Typography>
                </Grid>
              ) : filteredInventory.map(it => {
                const out = it.stockTracked && Number(it.stock || 0) <= 0;
                const inCart = cart.find(c => c.item.id === it.id);
                return (
                  <Grid item xs={6} sm={4} key={it.id}>
                    <Paper
                      variant="outlined"
                      onClick={() => !out && addToCart(it)}
                      sx={{
                        p: 1, cursor: out ? 'not-allowed' : 'pointer',
                        opacity: out ? 0.5 : 1,
                        borderRadius: 2,
                        '&:hover': out ? {} : { borderColor: MAIN_COLOR, boxShadow: 1 },
                      }}>
                      <Box sx={{
                        width: '100%', aspectRatio: '1 / 1', borderRadius: 1, overflow: 'hidden',
                        backgroundColor: '#f5f5f5', mb: 0.5,
                        backgroundImage: it.imageUrl || it.images?.[0] ? `url(${it.imageUrl || it.images?.[0]})` : 'none',
                        backgroundSize: 'cover', backgroundPosition: 'center',
                      }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{it.name}</Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                          ${Number(it.price || 0).toLocaleString()}
                        </Typography>
                        {out ? (
                          <Chip size="small" label="agot." color="error" sx={{ height: 18, fontSize: 10 }} />
                        ) : inCart ? (
                          <Chip size="small" label={`×${inCart.qty}`} color="primary" sx={{ height: 18, fontSize: 10 }} />
                        ) : null}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* DER: carrito + cliente + pago */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Cliente</Typography>
            <Autocomplete
              options={customers}
              value={customer}
              onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.mobile ? ` · ${u.mobile}` : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Buscar cliente registrado" />}
            />
            {!customer && (
              <>
                <Divider sx={{ my: 1.5 }}>o walk-in</Divider>
                <TextField
                  fullWidth size="small" placeholder="Nombre del cliente"
                  value={walkInName} onChange={(e) => setWalkInName(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth size="small" placeholder="Teléfono (opcional)"
                  value={walkInPhone} onChange={(e) => setWalkInPhone(e.target.value)}
                />
              </>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Carrito</Typography>
            {cart.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Toca un producto del catálogo.
              </Typography>
            ) : cart.map(({ item, qty }) => (
              <Stack direction="row" alignItems="center" spacing={1} key={item.id} sx={{ py: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${Number(item.price || 0).toLocaleString()} c/u
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => changeQty(item.id, -1)}><RemoveIcon fontSize="small"/></IconButton>
                <Typography variant="body2" sx={{ width: 22, textAlign: 'center', fontFamily: 'monospace' }}>{qty}</Typography>
                <IconButton size="small" onClick={() => changeQty(item.id, +1)}><AddIcon fontSize="small"/></IconButton>
                <Typography variant="body2" sx={{ width: 70, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                  ${(Number(item.price || 0) * qty).toLocaleString()}
                </Typography>
                <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}><DeleteIcon fontSize="small"/></IconButton>
              </Stack>
            ))}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                ${totals.subtotal.toLocaleString()}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography sx={{ fontWeight: 800, fontFamily: 'monospace', fontSize: 18 }}>
                {partner.currency || ''} ${totals.total.toLocaleString()}
              </Typography>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Pago</Typography>
            <TextField
              select fullWidth size="small" label="Método" value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              sx={{ mb: 1 }}
            >
              <MenuItem value="cash">Efectivo</MenuItem>
              <MenuItem value="card">Tarjeta</MenuItem>
              <MenuItem value="wallet">Billetera</MenuItem>
              <MenuItem value="transfer">Transferencia</MenuItem>
            </TextField>
            <Stack direction="row" spacing={1} alignItems="center">
              <input
                type="checkbox" id="paidchk" checked={paid}
                onChange={(e) => setPaid(e.target.checked)}
              />
              <label htmlFor="paidchk">
                <Typography variant="body2">Cobrado en caja</Typography>
              </label>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Entrega (opcional)</Typography>
            <TextField
              fullWidth size="small" placeholder="Dirección de entrega" value={dropAddress}
              onChange={(e) => setDropAddress(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Autocomplete
              options={drivers}
              value={assignedDriver}
              onChange={(_, v) => setAssignedDriver(v)}
              getOptionLabel={(u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.mobile ? ` · ${u.mobile}` : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Asignar conductor (opcional)" />}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Notas</Typography>
            <TextField
              fullWidth size="small" multiline minRows={2}
              placeholder="Ej: Sin cebolla, llamar al llegar…"
              value={notes} onChange={(e) => setNotes(e.target.value)}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <input
                type="checkbox" id="confchk" checked={confirmNow}
                onChange={(e) => setConfirmNow(e.target.checked)}
              />
              <label htmlFor="confchk">
                <Typography variant="body2">
                  Confirmar de una vez (saltar paso PENDING — el negocio recibe ya CONFIRMED)
                </Typography>
              </label>
            </Stack>
          </Paper>

          <Button
            fullWidth size="large" variant="contained"
            sx={{ backgroundColor: MAIN_COLOR, height: 56, fontSize: 16, fontWeight: 700 }}
            disabled={!canCreate || creating}
            onClick={onCreate}
          >
            {creating ? <CircularProgress size={22} color="inherit" /> : `Crear pedido · $${totals.total.toLocaleString()}`}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
